import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { activityMediaFilenameExtension, downloadRemoteFile } from '../utils/activityMediaUtils';

const ActivityMediaModal = ({ open, onClose, items, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [videoUi, setVideoUi] = useState({ showPlay: true });
  const [isDownloading, setIsDownloading] = useState(false);
  const videoRef = useRef(null);
  const itemCount = items?.length ?? 0;

  useLayoutEffect(() => {
    if (!open || itemCount === 0) return;
    setCurrentIndex(Math.min(Math.max(0, initialIndex), itemCount - 1));
  }, [open, initialIndex, itemCount]);

  useEffect(() => {
    if (!open) setIsDownloading(false);
  }, [open]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      setVideoUi({ showPlay: true });
    }
  }, [currentIndex]);

  const handleKey = useCallback(
    (e) => {
      if (!open || !items || items.length <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((prev) => (prev + 1) % items.length);
      }
    },
    [open, items],
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, handleKey]);

  const next = () => setCurrentIndex((prev) => (prev + 1) % items.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);

  const handleDownload = async () => {
    if (!items || items.length === 0 || isDownloading) return;
    const idx = Math.min(Math.max(0, currentIndex), items.length - 1);
    const item = items[idx];
    if (!item?.url) return;
    const ext = activityMediaFilenameExtension(item.url, item.type === 'video');
    const filename = `activity_media_${idx + 1}.${ext}`;
    setIsDownloading(true);
    try {
      const usedBlob = await downloadRemoteFile(item.url, filename);
      if (!usedBlob) {
        window.alert(
          'Could not download directly (browser blocked the file). A new tab may have opened — use Save as there, or use Download on selected posts from the activity toolbar.',
        );
      }
    } catch (err) {
      console.error('Media modal download failed:', err);
      window.alert('Download failed. Check your connection and try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleModalVideo = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      void v.play();
    } else {
      v.pause();
    }
  };

  if (!open || !items || items.length === 0) return null;

  const idx = Math.min(Math.max(0, currentIndex), items.length - 1);
  const item = items[idx];
  if (!item?.url) return null;
  const isVideo = item.type === 'video';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="presentation"
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--cc-card-bg)',
          borderRadius: 14,
          padding: 0,
          width: 'min(92vw, 1120px)',
          minWidth: 340,
          maxWidth: '92vw',
          maxHeight: '90vh',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          position: 'relative',
          boxSizing: 'border-box',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Media viewer"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 28px 10px 28px',
            borderBottom: '1px solid var(--branding-app-surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              style={{
                background: hoveredButton === 'download' ? 'var(--branding-app-primary-hover)' : 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: isDownloading ? 'wait' : 'pointer',
                fontSize: 14,
                transition: 'background-color 0.2s',
                opacity: isDownloading ? 0.85 : 1,
              }}
              onMouseEnter={() => setHoveredButton('download')}
              onMouseLeave={() => setHoveredButton(null)}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </button>
            <span style={{ fontWeight: 600, fontSize: 18, color: 'var(--cc-text-primary)' }}>
              {items.length > 1 ? `${currentIndex + 1} of ${items.length}` : ''}
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 14,
                  color: 'var(--cc-text-muted)',
                  marginLeft: items.length > 1 ? 8 : 0,
                }}
              >
                {items.length > 1 ? ' · ' : ''}
                {isVideo ? 'Video' : 'Photo'}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'var(--cc-cancel-bg)',
              color: 'var(--cc-text-primary)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Close
          </button>
        </div>
        <div
          style={{
            padding: 24,
            overflow: 'auto',
            maxHeight: '75vh',
            minHeight: isVideo ? 320 : 300,
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            background: isVideo ? '#0f172a' : undefined,
          }}
        >
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  zIndex: 10,
                }}
              >
                ‹
              </button>
              <button
                type="button"
                onClick={next}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  zIndex: 10,
                }}
              >
                ›
              </button>
            </>
          )}
          {isVideo ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                maxHeight: 'min(72vh, calc(90vh - 140px))',
                minHeight: 'min(40vh, 360px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={toggleModalVideo}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleModalVideo(e);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={videoUi.showPlay ? 'Play video' : 'Pause video'}
            >
              <video
                key={item.url}
                ref={videoRef}
                src={item.url}
                playsInline
                preload="metadata"
                onPlay={() => setVideoUi({ showPlay: false })}
                onPause={() => setVideoUi({ showPlay: true })}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: 'min(72vh, calc(90vh - 140px))',
                  borderRadius: 8,
                  background: '#000',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {videoUi.showPlay && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.35) 100%)',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.95)',
                      color: 'var(--branding-app-primary, #2563eb)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                      paddingLeft: 4,
                    }}
                    aria-hidden
                  >
                    ▶
                  </div>
                </div>
              )}
            </div>
          ) : (
            <img
              src={item.url}
              alt="Work upload"
              style={{
                maxWidth: '90vw',
                maxHeight: '70vh',
                borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                background: 'var(--branding-app-surface-panel)',
                objectFit: 'contain',
              }}
            />
          )}
        </div>
        {items.length > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 8,
              padding: '16px 24px',
              borderTop: '1px solid var(--branding-app-surface)',
              flexWrap: 'wrap',
            }}
          >
            {items.map((it, index) => (
              <button
                key={`${it.type}-${it.url}-${index}`}
                type="button"
                onClick={() => setCurrentIndex(index)}
                title={it.type === 'video' ? 'Video' : 'Photo'}
                style={{
                  width: it.type === 'video' ? 10 : 8,
                  height: it.type === 'video' ? 10 : 8,
                  borderRadius: it.type === 'video' ? 2 : '50%',
                  background: index === currentIndex ? 'var(--branding-app-primary)' : '#cbd5e1',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityMediaModal;
